import { DOCUMENT } from '@angular/common';
import {
    Inject,
    Injectable,
    NgZone,
} from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    PageMetadata,
    PageMetadataRegistry,
} from '@app/router';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import { skip } from 'rxjs/operators';

type MutableWindow = Window & Record<string, any>;

@Injectable()
export class AnalyticsService {
    private _title?: string;
    private _window: MutableWindow;

    constructor(
        @Inject(DOCUMENT)
        document: Document,
        private _zone: NgZone,
        private _appState: AppStateFacade,
        private _pageRegistry: PageMetadataRegistry,
    ) {
        this._window = document.defaultView!;

        this._pageRegistry
            .activePage$
            // skip initial emissions
            .pipe(
                skip(2),
                filterNullOrUndefined(),
            )
            .subscribe((page: PageMetadata) => {
                const title = page.title;
                const parentTitle = page.parent ? page.parent.title : null;

                const currentPage = parentTitle ? `${parentTitle} ${title}` : title;
                this._title = currentPage || 'Dashboard';

                this.track('Page Load');
            });
    }

    track(name: string, args?: any) {
        this._zone.runOutsideAngular(() => {
            const argsCopy = Object.assign({}, args);
            this._parseColumnsFilter(argsCopy);
            this._mixPanelTrack(
                name,
                Object.assign({
                    modernFolders: !!this._appState.settings.get(Setting.isModernFolderSystemEnabled),
                    language: this._appState.language.value,
                    page: this._title,
                    version: this._appState.authConfig.buildVersion,
                }, argsCopy),
            );
        });
    }

    private _mixPanelTrack(...args: any[]) {
        if (this._window['mixpanel']) {
            this._window['mixpanel'].track.apply(this._window['mixpanel'], args);
        }
    }

    private _parseColumnsFilter(filters: any) {
        const columnFilters = filters['columnFilters'];
        const displayColumnFilters: any = [];
        if (columnFilters) {
            Object.keys(columnFilters).forEach(key => {
                const columnFilter = columnFilters[key];
                if (columnFilter.value === null) {
                    delete columnFilters[key];
                } else if (columnFilter.label) {
                    displayColumnFilters.push(`${columnFilter.title} ${columnFilter.operator} ${columnFilter.label}`);
                } else {
                    displayColumnFilters.push(`${columnFilter.title} ${columnFilter.operator} ${columnFilter.value}`);
                }
            });

            if (displayColumnFilters.length) {
                filters['columnFilters'] = displayColumnFilters.join('; ');
            } else {
                delete filters['columnFilters'];
            }
        }
    }
}
